// react core
import { useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router";

// aws tools
import configAws from "components/config/ConfigAws";
import { Amplify } from "aws-amplify";
import { Hub } from "aws-amplify/utils";
import { CookieStorage } from "aws-amplify/utils";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { AwsRum } from "aws-rum-web";

// datadog tools
import { datadogRum } from "@datadog/browser-rum";

// material theme
import mainTheme from "components/main/MainTheme";
import { ThemeProvider, responsiveFontSizes } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// material date picker
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// material design
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Fade from "@mui/material/Fade";
import Switch from "@mui/material/Switch";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsUp as iconYes } from "@fortawesome/pro-duotone-svg-icons";
import { faThumbsDown as iconNo } from "@fortawesome/pro-duotone-svg-icons";
import { faWifiSlash as iconOffline } from "@fortawesome/pro-duotone-svg-icons";
import { faSyncAlt as iconVersion } from "@fortawesome/pro-duotone-svg-icons";

// entzy models and services
import { createMainMenuList } from "models/main";
import { serviceSyncUser, serviceCookieGet } from "services/graphql/call";
import { scrollSnapTop, jsonTryParse } from "services/tools";

// entzy config and components
import configEntzy from "components/config/ConfigEntzy";
import MainHeader from "components/main/MainHeader";
import MainContent from "components/main/MainContent";
import MenuBox from "components/menu/MenuBox";
import ConnectBox from "components/connect/ConnectBox";
import { AppLoader, ActionLoader } from "components/utils/common/CommonLoaders";
import { ActionBoxButton } from "components/utils/common/CommonButtons";

// entzy hooks
import { useIdentityCookie, useIdentityState } from "hooks/identity";
import {
  useMainActions,
  useMainBackdrop,
  useMainSections,
  useMainVersion,
} from "hooks/main";
import { useMediaMaps } from "hooks/media";

// cookie consent
const userCookieConsent = serviceCookieGet("cookieConsent") !== "reject";

// aws amplify configuration
Amplify.configure({
  Auth: configAws.Auth,
  Storage: configAws.Storage.read,
  API: configAws.API,
});
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: configEntzy.APP_COOKIE_DOMAIN,
    path: "/",
    expires: 365,
    secure: configEntzy.IS_LOCAL ? false : true,
  }),
);

// aws amplify auth event listener
Hub.listen("auth", ({ payload: { event, data } }) => {
  switch (event) {
    case "signedIn":
      // this signedIn event is triggered for all user connect entry points
      // signup / signin / federated (along with signInWithRedirect event)
      // optional data object received:
      // - { userId (sub), signInDetails.loginId (email) .authFlowType }
      serviceSyncUser("signin");
      scrollSnapTop();

      // event tracking
      if (userCookieConsent && !configEntzy.IS_LOCAL) {
        // google analytics
        if (window && window.gtag) {
          window.gtag("event", "signin", {
            send_to: configEntzy.GOOGLE_TAG_CONVERSION_SIGNIN,
          });
        }
        // x analytics
        if (window && window.twq) {
          window.twq("event", "tw-r6hyb-r6hyh", {
            value: "signin",
          });
        }
      }

      break;

    case "signInWithRedirect":
      // optional additional handlers for federated connections
      break;

    case "customOAuthState":
      // optional additional handlers for custom oauth state from federated connections
      if (data) {
        const authState = jsonTryParse(data, {});
        const authType = authState.type;
        const authProvider = authState.provider;
        if (authType === "amplify") {
          console.debug("IDP: " + authProvider);
        }
      }
      break;

    default:
      break;
  }
});

// realtime user monitoring
if (userCookieConsent && !configEntzy.IS_LOCAL) {
  try {
    datadogRum.init({
      applicationId: configEntzy.DATATDOG_RUM_APP_ID,
      clientToken: configEntzy.DATATDOG_RUM_CLIENT_TOKEN,
      site: configEntzy.DATATDOG_RUM_SITE,
      service: configEntzy.DATATDOG_RUM_SERVICE,
      env: configEntzy.APP_ENV,
      version: configEntzy.APP_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
    });
  } catch (error) {
    console.warn("Failed to initialize Datadog RUM");
  }

  try {
    const configRumAws = {
      identityPoolId: configAws.Auth.Cognito.identityPoolId,
      endpoint: `https://dataplane.rum.${configAws.Auth.Cognito.region}.amazonaws.com`,
      telemetries: ["errors", "performance", "http"],
      sessionSampleRate: 1,
      allowCookies: true,
      enableXRay: true,
      signing: true,
      enableDebug: true,
    };
    new AwsRum(
      configAws.Auth.Cognito.rumAppMonitorId,
      "1.0.0",
      configAws.Auth.Cognito.region,
      configRumAws,
    );
  } catch (error) {
    console.warn("Failed to initialize AWS RUM");
  }
}

// app constants
const mainMenuList = createMainMenuList();

function App(props) {
  // hooks: core
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // hooks: identity
  const [cookieConsent, handleCookieConsent] = useIdentityCookie();
  const [identifying, user, userReload] = useIdentityState();

  // hooks: main
  const [reloadKeys, sectionReload] = useMainSections({ user });
  const [
    actionLoading,
    appAlert,
    appAlertClear,
    autoJoinEvent,
    autoJoinEventClear,
  ] = useMainActions({
    user,
    userReload,
    navigate,
    location,
    searchParams,
  });
  const [backdropActive, setBackdrop] = useMainBackdrop({ user });
  const [online, hasUpdate, latestVersion] = useMainVersion();

  // hooks: media
  // activate google maps api
  useMediaMaps(props);

  // state: main
  const [drawerMenu, setDrawerMenu] = useState(false);
  const [drawerConnect, setDrawerConnect] = useState(false);
  const [menuSelected, setMenuSelected] = useState(
    mainMenuList.filter((obj) => obj.nav === props.page)[0],
  );

  // handlers: menu and connecting
  const drawerMenuToggle = (open) => {
    const newOpen = typeof open == "boolean" ? open : !drawerMenu;
    scrollSnapTop();
    setDrawerMenu(newOpen);
    if (drawerConnect && newOpen) {
      setDrawerConnect(false);
    }
  };
  const drawerConnectToggle = (open) => {
    const newOpen = typeof open == "boolean" ? open : !drawerConnect;
    scrollSnapTop();
    setDrawerConnect(newOpen);
    if (drawerMenu && newOpen) {
      setDrawerMenu(false);
    }
  };
  const connectionStart = (connecting) => {
    navigate("/connecting?flow=" + connecting.split(":")[1]);
  };

  // handlers: menu
  // overrides for drawer actions and external connection flows
  // otherwise navigate to menu selection and scroll page top
  const menuSelect = (selection) => {
    if (selection.id === "menu") {
      drawerMenuToggle(true);
    } else if (selection.id === "connect") {
      drawerConnectToggle(true);
    } else {
      setMenuSelected(selection);
      navigate(selection.nav);
      drawerMenuToggle(false);
      if (selection.parent) {
        scrollSnapTop();
      }
    }
    sectionReload(["header"]);
  };
  const menuSelectById = (id) => {
    if (id.startsWith("connecting:")) {
      return connectionStart(id);
    } else {
      const selection = mainMenuList.filter((obj) => obj.id === id)[0];
      return menuSelect(selection);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={responsiveFontSizes(mainTheme)}>
        <CssBaseline />
        <Box id="anchor-main-test" className="box-default"></Box>
        <Box
          id="anchor-main-app"
          className="box-default full-height bg-black-t75"
        >
          {identifying || actionLoading === 2 ? (
            <AppLoader />
          ) : (
            <Box className="box-default">
              <MainHeader
                key={reloadKeys.header}
                page={props.page}
                pageParentMenuId={props.pageParentMenuId}
                drawerMenu={drawerMenu}
                drawerConnect={drawerConnect}
                drawerMenuToggle={drawerMenuToggle}
                drawerConnectToggle={drawerConnectToggle}
                sectionReload={sectionReload}
                menuSelect={menuSelect}
                menuSelectById={menuSelectById}
                menuSelected={menuSelected}
                user={user}
                userReload={userReload}
              />

              {actionLoading === 1 && (
                <Box className="box-default zero-height">
                  <Box
                    className="box-fixed bottom-left full-width"
                    sx={{ pb: configEntzy.APP_SPACING_MD2X }}
                    hidden={true}
                  >
                    <ActionLoader size="sm" />
                  </Box>
                </Box>
              )}

              <Fade in={drawerMenu} timeout={1000}>
                <Box
                  className="box-default"
                  hidden={!drawerMenu}
                  sx={{
                    p: configEntzy.APP_SPACING_MD,
                  }}
                >
                  <MenuBox
                    key={reloadKeys.menu}
                    navigate={navigate}
                    drawerMenuToggle={drawerMenuToggle}
                    drawerConnectToggle={drawerConnectToggle}
                    sectionReload={sectionReload}
                    menuSelect={menuSelect}
                    menuSelectById={menuSelectById}
                    menuSelected={menuSelected}
                    backdropActive={backdropActive}
                    setBackdrop={setBackdrop}
                    user={user}
                    userReload={userReload}
                  />
                </Box>
              </Fade>

              <Fade in={drawerConnect} timeout={1000}>
                <Box
                  className="box-default"
                  hidden={!drawerConnect}
                  sx={{
                    p: configEntzy.APP_SPACING_MD,
                  }}
                >
                  <ConnectBox
                    key={reloadKeys.connect}
                    navigate={navigate}
                    drawerConnectToggle={drawerConnectToggle}
                    sectionReload={sectionReload}
                    menuSelectById={menuSelectById}
                    user={user}
                    userReload={userReload}
                  />
                </Box>
              </Fade>

              <Fade in={!drawerMenu && !drawerConnect} timeout={1000}>
                <Box
                  className="box-default"
                  hidden={drawerMenu || drawerConnect}
                >
                  <MainContent
                    key={reloadKeys.content}
                    page={props.page}
                    pageParentMenuId={props.pageParentMenuId}
                    hideMenu={props.hideMenu}
                    searchParams={searchParams}
                    navigate={navigate}
                    drawerMenu={drawerMenu}
                    drawerConnect={drawerConnect}
                    drawerMenuToggle={drawerMenuToggle}
                    drawerConnectToggle={drawerConnectToggle}
                    sectionReload={sectionReload}
                    menuSelect={menuSelect}
                    menuSelectById={menuSelectById}
                    menuSelected={menuSelected}
                    user={user}
                    userReload={userReload}
                    appAlert={appAlert}
                    appAlertClear={appAlertClear}
                    autoJoinEvent={autoJoinEvent}
                    autoJoinEventClear={autoJoinEventClear}
                  />
                </Box>
              </Fade>
            </Box>
          )}
          {/* offline and version checker */}
          {online === false && (
            <Box
              className="box-fixed bottom full-width bg-black-t90 action-pointer"
              sx={{
                p: configEntzy.APP_SPACING_MD,
                borderTop: "1px solid rgba(255,255,255,0.25)",
              }}
              onClick={() => window.location.reload(true)}
            >
              <Typography variant="h6" color="warning">
                <FontAwesomeIcon icon={iconOffline} />
              </Typography>
              <Typography variant="subtitle2" color="warning">
                You are currently offline. Tap here to retry.
              </Typography>
            </Box>
          )}
          {online && hasUpdate && (
            <Box
              className="box-fixed bottom full-width bg-black-t90 action-pointer"
              sx={{
                p: configEntzy.APP_SPACING_MD,
                borderTop: "1px solid rgba(255,255,255,0.25)",
              }}
              onClick={() => window.location.reload(true)}
            >
              <Typography variant="h6" color="warning">
                <FontAwesomeIcon icon={iconVersion} />
              </Typography>
              <Typography variant="subtitle2" hidden={true}>
                {latestVersion}
              </Typography>
              <Typography variant="subtitle2">
                New version available. Tap here to load latest.
              </Typography>
            </Box>
          )}
          {/* cookie consent */}
          {!identifying && !user.connected && (
            <Box className="box-default">
              {cookieConsent ? (
                <Box
                  className="box-fixed bottom"
                  sx={{
                    pb: configEntzy.APP_SPACING_MD,
                  }}
                >
                  <FormControl component="fieldset">
                    <FormGroup aria-label="position" row>
                      <FormControlLabel
                        value="top"
                        control={
                          <Switch
                            color="success"
                            checked={cookieConsent === "accept"}
                            onChange={(event) => {
                              handleCookieConsent(
                                event.target.checked ? "accept" : "reject",
                              );
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="subtitle2"
                            color={
                              cookieConsent === "accept" ? "success" : "white"
                            }
                          >
                            Cookies
                          </Typography>
                        }
                        labelPlacement="top"
                      />
                    </FormGroup>
                  </FormControl>
                </Box>
              ) : (
                <Box
                  className="box-fixed bottom full-width bg-black-t90 pop-top"
                  sx={{
                    p: configEntzy.APP_SPACING_MD,
                    borderTop: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  <Container maxWidth="lg">
                    <Box className="box-default">
                      <Box
                        className="box-default"
                        sx={{
                          pr: configEntzy.APP_SPACING_SM,
                          width: "20%",
                        }}
                      >
                        <ActionBoxButton
                          size="small"
                          text={
                            <span>
                              <FontAwesomeIcon
                                icon={iconNo}
                                transform="grow-8"
                              />
                              <span>&nbsp;&nbsp;&nbsp;NO</span>
                            </span>
                          }
                          bgColor="transparent"
                          color="danger.dark"
                          onClick={() => handleCookieConsent("reject")}
                        />
                      </Box>
                      <Box
                        className="box-default"
                        sx={{
                          pt: configEntzy.APP_SPACING_MD,
                          width: "60%",
                          color: "white",
                        }}
                      >
                        <Typography variant="body2">
                          Are performance cookies ok?
                        </Typography>
                      </Box>
                      <Box
                        className="box-default"
                        sx={{
                          pl: configEntzy.APP_SPACING_SM,
                          width: "20%",
                        }}
                      >
                        <ActionBoxButton
                          size="small"
                          text={
                            <span>
                              <span>OK&nbsp;&nbsp;&nbsp;</span>
                              <FontAwesomeIcon
                                icon={iconYes}
                                transform="grow-4"
                              />
                            </span>
                          }
                          bgColor="transparent"
                          color="success.dark"
                          onClick={() => handleCookieConsent("accept")}
                        />
                      </Box>
                    </Box>
                  </Container>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
